/**
 * Левая сторона: вертикальный рельс пространств + колонка со списком чатов.
 *
 * Решение Антона 2026-09-09 18:11 UTC: «левую сторону делаем в стиле телеграм»,
 * уточнение 18:14 UTC: «Телеграм как расположение фильтров наверху»,
 * уточнение 19:08 UTC: рельс слева, как в Element classic, ОДНОВРЕМЕННО с рядом.
 *
 * ДВА СПОСОБА ВЫБРАТЬ ОДНО И ТО ЖЕ — ЭТО НЕ ОШИБКА ДИЗАЙНА.
 * Рельс слева и ряд вкладок сверху управляют ОДНИМ состоянием `tab`. Кликнул
 * спейс в рельсе — подсветилась его вкладка в ряду, и наоборот. Именно так
 * устроен скриншот Антона, и дублирование здесь сознательное: рельс даёт
 * постоянную «карту» пространств одним взглядом, ряд — быстрый доступ к
 * системным фильтрам (Непрочитанные / Избранное / Личные), которых в рельсе нет.
 *
 * Что здесь важно по устройству:
 *  - строка чата имеет ФИКСИРОВАННУЮ высоту (theme.size.rowHeight = 60px). Это
 *    не косметика: одинаковая высота строк — единственное, что удерживает
 *    плотную компоновку, когда имя или превью длинные. Поэтому и имя, и превью
 *    жёстко в одну строку с ellipsis.
 *  - вкладки спейсов строятся ДИНАМИЧЕСКИ из данных клиента. Хардкода нет:
 *    добавили спейс в Element — вкладка появилась сама, без правки кода.
 *  - фильтрация и поиск живут в этом компоненте: они не меняют состояние на
 *    сервере и не нужны никому выше.
 *  - показ списка НИЧЕГО не отмечает прочитанным. Отметки ставит только RoomView
 *    при реальном чтении (окно в фокусе + лента внизу) — не трогаем эту логику.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { PanelResizeHandle, usePanelWidth } from "./PanelResize";
import type { Messages } from "../i18n/messages";
import { currentLocale, useT, useTF } from "../i18n";

type TKey = keyof Messages;

import { useMatrixClient } from "../matrix/clientContext";
import { useAuthedAvatar } from "../matrix/media";
import { isHomeRoom, type RoomLists, type RoomSummary, type SpaceInfo } from "../matrix/rooms";
import { MessageSearchResults } from "./MessageSearchResults";
import { RoomMenu, type RoomMenuTarget } from "./RoomMenu";
import { SpaceMenu, type SpaceMenuTarget } from "./SpaceMenu";
import { IconHome, IconPlus, IconSettings } from "./icons";
import { roomListCss } from "./roomListCss";
import { s, useNarrowScreen } from "./styles";
import { color, colorOf, font, radius, size, space as sp } from "./theme";

/**
 * Идентификатор выбранного фильтра. ОДНО состояние на рельс и на ряд.
 *
 * Вкладка спейса кодируется строкой "space:<roomId>", чтобы все фильтры жили
 * в одном значении и были взаимно исключающими без отдельной логики.
 *
 * MENTIONS: добавлено одной строкой в тип и одним объектом в `tabs` ниже,
 * как и было предусмотрено. Фильтр — по r.highlight > 0 (серверный счётчик
 * упоминаний, MSC3952), а не по тексту превью.
 */
type Tab = "home" | `space:${string}`;
type ChatFilter = "unread" | "mentions" | "favorite" | "groups" | "people" | "waiting";

/**
 * Ключ localStorage под свёрнутость левой колонки (Антон 2026-09-18, п.8).
 * Префикс `alg.` — как у всех остальных ключей клиента.
 */
const ROOM_LIST_COLLAPSED_KEY = "alg.roomListCollapsed";

interface TabDef {
  id: Tab;
  label: string;
  /** Счётчик в самой вкладке; 0 — не показывать. */
  count: number;
  /** Отбор комнат для этой вкладки. */
  filter: (r: RoomSummary) => boolean;
  /** Текст пустого состояния — у каждой вкладки свой, честный. */
  empty: string;
}

interface FilterDef {
  id: ChatFilter;
  label: string;
  filter: (r: RoomSummary) => boolean;
  empty: string;
}

function Avatar({ room }: { room: RoomSummary }) {
  // Аватар качается с токеном (сервер отдаёт media только авторизованно),
  // пока идёт загрузка или если аватара нет — инициалы.
  const client = useMatrixClient();
  // Размер передаём явно (п.9, 2026-09-18): хук сам запросит превью ≥2××DPR
  // с method=crop. Без него тянулся оригинал и сжимался браузером.
  const url = useAuthedAvatar(client, room.avatarUrl, size.avatar);

  if (url) {
    return <img src={url} alt="" style={s.avatar} />;
  }
  return (
    <div
      style={{ ...s.avatar, background: colorOf(room.roomId), color: color.textOnAccent }}
      aria-hidden
    >
      {room.initials}
    </div>
  );
}

/**
 * Круглая иконка в вертикальном рельсе: аватар пространства, при отсутствии —
 * инициалы на детерминированном по room_id цвете (та же функция, что у комнат,
 * поэтому цвет спейса в рельсе и цвет его комнаты в списке живут в одной гамме).
 */
function RailIcon({
  id,
  label,
  initials,
  avatarUrl,
  badge,
  highlightBadge,
  active,
  onClick,
  glyph,
  onContextMenu,
}: {
  id: string;
  label: string;
  initials: string;
  avatarUrl?: string | null;
  badge: number;
  /** Упоминания внутри этого пространства/Home — красный бейдж рядом с обычным. */
  highlightBadge?: number;
  active: boolean;
  onClick: () => void;
  /** Для Home: иконка вместо инициалов (SVG, не текстовый символ). */
  glyph?: ReactNode;
  /**
   * Правый клик по иконке (меню пространства). Только для спейсов: у Home
   * меню нет — там нечего настраивать, он не комната на сервере.
   */
  onContextMenu?: (x: number, y: number) => void;
}) {
  const t = useT();
  const client = useMatrixClient();
  /* АВАТАРЫ РЕЛЬСА СПЕЙСОВ — ровно то место, где Антон увидел размытие
     (скрин 2026-09-18 12:21, логотип «alg»). size.railIcon — ОТОБРАЖАЕМЫЙ размер
     (40px CSS); хук запросит превью ≥2× от него с учётом devicePixelRatio
     (на Retina это 160px, на обычном экране — 96px после округления до шага). */
  const url = useAuthedAvatar(client, avatarUrl ?? null, size.railIcon);

  const inner =
    url ? (
      <img
        className="alg-rail-icon"
        src={url}
        alt=""
        style={s.railIconInner}
      />
    ) : (
      <div
        className="alg-rail-icon"
        style={{
          ...s.railIconInner,
          background: glyph ? color.tgBlue : colorOf(id),
          // У спейсов initials — ОДНА буква (spaceLetterOf, правка Антона
          // 2026-09-21); 17px на круге 40px читается как аватар-заглушка.
          // У Home внутри SVG-домик, и fontSize на него не действует.
          fontSize: 17,
        }}
        aria-hidden
      >
        {glyph ?? initials}
      </div>
    );

  return (
    <button
      type="button"
      className={"alg-rail-btn" + (active ? " is-active" : "")}
      onClick={onClick}
      onContextMenu={
        onContextMenu
          ? (e) => {
              // Своё меню спейса вместо браузерного — тот же приём, что
              // у строки чата (см. Row): жест привычен из Element.
              e.preventDefault();
              onContextMenu(e.clientX, e.clientY);
            }
          : undefined
      }
      title={label}
      aria-label={label}
      aria-pressed={active}
      data-testid={"rail-item"}
      data-rail-id={id}
      data-rail-active={active ? "1" : "0"}
    >
      {inner}
      {/* Красный бейдж упоминаний — в ВЕРХНЕМ углу иконки: обычный живёт
          в нижнем (s.railBadge), и два бейджа не наезжают друг на друга.
          Упоминание важнее — его и делаем заметнее по позиции. */}
      {(highlightBadge ?? 0) > 0 && (
        <span
          style={{
            ...s.railBadge,
            background: color.badgeHighlight,
            bottom: "auto",
            top: 0,
          }}
          data-testid="rail-badge-highlight"
          title={t("room.mentioned")}
        >
          {(highlightBadge ?? 0) > 99 ? "99+" : highlightBadge}
        </span>
      )}
      {badge > 0 && (
        <span style={s.railBadge} data-testid="rail-badge">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}

/**
 * Время последнего сообщения, как в Telegram:
 * сегодня — ЧЧ:ММ, вчера — «вчера», в этом году — «12 мая», раньше — 12.05.24.
 */
export function timeLabel(ts: number, t: (k: TKey) => string, now: Date = new Date()): string {
  if (!ts) return "";
  const d = new Date(ts);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d.getTime() >= startOfToday) {
    return d.toLocaleTimeString(currentLocale(), { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  }
  if (d.getTime() >= startOfToday - 86_400_000) return t("timeline.dayYesterday");
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(currentLocale(), { day: "numeric", month: "short" }).replace(".", "");
  }
  return d.toLocaleDateString(currentLocale(), { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/**
 * СТРОКА ЧАТА В СУЖЕННОЙ КОЛОНКЕ — один аватар (Антон 2026-09-18, п.8:
 * «левую колонку можно сужать вплоть до одних аватаров»).
 *
 * Отдельный компонент, а не ветка внутри Row: в свёрнутом виде нет ни
 * имени, ни времени, ни превью, ни двухэтажной вёрстки — общая разметка
 * с пятью `hidden` была бы грязнее тридцати строк. Всё, что ушло из вида,
 * остаётся доступным: имя и превью — в `title`, счётчики — бейджем на аватаре
 * (тот же приём, что у иконок спейсов в рельсе), правый клик — то же меню.
 */
/**
 * ДЛИННЫЙ ТАП ПО СТРОКЕ ЧАТА = «правый клик» (правка Антона 2026-09-20
 * 19:23): на тач-экране contextmenu нет, поэтому меню комнаты открывается
 * удержанием пальца 500мс — тот же жест, что у иконок спейсов в
 * горизонтальной строке. Возвращает обработчики для <button> строки.
 * Движение пальца (скролл списка) отменяет таймер — иначе каждый
 * пролистанный чат открывал бы меню.
 */
function useLongPressMenu(onMenu: (x: number, y: number) => void) {
  const timerRef = useRef<number | null>(null);
  const firedRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const cancel = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  return {
    onPointerDown: (e: React.PointerEvent) => {
      // Мышь имеет правый клик — длинный тап ей не нужен, иначе удержание
      // ЛКМ открыло бы второе меню поверх.
      if (e.pointerType === "mouse") return;
      firedRef.current = false;
      cancel();
      const startX = e.clientX;
      const startY = e.clientY;
      startXRef.current = startX;
      startYRef.current = startY;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        firedRef.current = true;
        onMenu(startX, startY);
      }, 500);
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onPointerMove: (e: React.PointerEvent) => {
      // Палец пошёл в скролл — меню не нужно. Сравниваем с точкой
      // старта (movement* в Safari отсутствует): порог 10px, мелкая
      // дрожь пальца на месте скроллом не считается.
      if (timerRef.current === null) return;
      if (Math.abs(e.clientY - startYRef.current) > 10 || Math.abs(e.clientX - startXRef.current) > 10) cancel();
    },
    onClickCapture: (e: React.SyntheticEvent) => {
      // Меню уже открылось — не даём тапу дополнительно ОТКРЫТЬ комнату.
      if (firedRef.current) {
        firedRef.current = false;
        e.preventDefault();
        e.stopPropagation();
      }
    },
  };
}

function CollapsedRow({
  room,
  active,
  onSelect,
  onMenu,
}: {
  room: RoomSummary;
  active: boolean;
  onSelect: (roomId: string) => void;
  onMenu: (room: RoomSummary, x: number, y: number) => void;
}) {
  const t = useT();
  const preview = room.previewText || (room.invite ? "" : t("room.noMessagesPreview"));
  // В свёрнутом виде title — единственный способ узнать чат без разворачивания,
  // поэтому кладём туда и имя, и превью.
  const hint = preview ? `${room.name}\n${room.previewSender ? room.previewSender + ": " : ""}${preview}` : room.name;
  // Длинный тап (тач) = правый клик: меню комнаты dropdown'ом под строкой (19:24).
  const longPress = useLongPressMenu((x, y) => onMenu(room, x, y));
  return (
    <button
      type="button"
      className={"alg-room-row is-collapsed" + (active ? " is-active" : "")}
      onClick={() => onSelect(room.roomId)}
      {...longPress}
      onContextMenu={(e) => {
        e.preventDefault();
        onMenu(room, e.clientX, e.clientY);
      }}
      data-testid="room-row"
      data-room-id={room.roomId}
      data-collapsed="1"
      data-pinned={room.pinned ? "1" : "0"}
      data-has-new={room.hasNewMessages ? "1" : "0"}
      title={hint}
      aria-label={room.name}
    >
      <span style={{ position: "relative", display: "inline-flex", flex: "0 0 auto" }}>
        <Avatar room={room} />
        {/* Счётчики переезжают на аватар: строки с цифрой справа больше нет.
            Красный (упоминания) вытесняет синий: два бейджа на 40px кружке
            налезают друг на друга и не читаются ни один. */}
        {room.highlight > 0 ? (
          // «@» вместо цифры (Антон 12.10 15:12): цифра на красном читалась
          // как обычный счётчик, и упоминание терялось среди прочих бейджей.
          <span
            className="alg-collapsed-badge is-mention"
            style={{ background: color.badgeHighlight }}
            data-testid="room-highlight"
            data-highlight={room.highlight}
            title={t("room.mentioned")}
            aria-label={t("room.mentioned")}
            role="img"
          >
            @
          </span>
        ) : room.notifying > 0 ? (
          <span className="alg-collapsed-badge" data-testid="room-badge">
            {room.notifying > 9 ? "9+" : room.notifying}
          </span>
        ) : room.hasNewMessages ? (
          // Жирного текста здесь нет, поэтому «есть новое, но не мне» показываем
          // точкой — иначе в свёрнутом виде эта информация теряется совсем.
          <span className="alg-collapsed-dot" data-testid="room-newdot" aria-hidden />
        ) : null}
      </span>
    </button>
  );
}

function Row({
  room,
  active,
  onSelect,
  onMenu,
}: {
  room: RoomSummary;
  active: boolean;
  onSelect: (roomId: string) => void;
  /** Правый клик по строке — контекстное меню комнаты. */
  onMenu: (room: RoomSummary, x: number, y: number) => void;
}) {
  const t = useT();
  const preview = room.previewText || (room.invite ? "" : t("room.noMessagesPreview"));
  // МОДЕЛЬ 13:49: жирный — «есть новое, но не мне» (шум, hasNewMessages),
  // циферка — «меня ждут» (notifying). Жирным рисуем и строку с бейджем —
  // как в Telegram: бейдж + обычный текст читается хуже. hasNewMessages
  // взаимоисключает notifying === 0 (см. rooms.ts/summarize).
  const nameWeight = room.notifying > 0 || room.hasNewMessages
    ? font.weightBold
    : undefined;
  // Длинный тап (тач) = правый клик: меню комнаты dropdown'ом под строкой (19:24).
  const longPress = useLongPressMenu((x, y) => onMenu(room, x, y));
  return (
    <button
      type="button"
      className={"alg-room-row" + (active ? " is-active" : "")}
      onClick={() => onSelect(room.roomId)}
      {...longPress}
      onContextMenu={(e) => {
        // Своё меню вместо браузерного: в браузерном нет ничего полезного
        // для чата, а правый клик по строке — привычный жест из Telegram/Element.
        e.preventDefault();
        onMenu(room, e.clientX, e.clientY);
      }}
      data-testid="room-row"
      data-room-id={room.roomId}
      data-pinned={room.pinned ? "1" : "0"}
      data-thread-unread={room.threadUnread}
      data-has-new={room.hasNewMessages ? "1" : "0"}
      title={room.name}
    >
      <Avatar room={room} />
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={s.rowTop}>
          <span
            className="alg-room-name"
            style={{ ...s.roomName, ...(nameWeight ? { fontWeight: nameWeight } : {}) }}
            data-testid="room-name"
          >
            {room.encrypted && <span title={t("room.encryptedBadge")}>🔒 </span>}
            {room.name}
          </span>
          <span className="alg-room-time" style={s.roomTime}>
            {room.pinned && <span title={t("room.favourite")}>★ </span>}
            {timeLabel(room.lastTs, t)}
          </span>
        </div>
        <div style={s.rowBottom}>
          <span
            className="alg-room-preview"
            style={{
              ...s.roomPreview,
              ...(nameWeight ? { fontWeight: nameWeight, color: color.text } : {}),
            }}
            data-testid="room-preview"
          >
            {room.previewSender && (
              <span className="alg-room-sender" style={s.previewSender}>
                {room.previewSender}:{" "}
              </span>
            )}
            {preview}
          </span>
          {/* Красный бейдж упоминаний идёт ПЕРЕД синим: если тебя назвали по
              имени, это важнее общего числа непрочитанного. */}
          {/* «@» — отдельный круглый значок без цифры, как в Telegram
              (Антон 12.10 15:12 «не вижу упоминания себя»). Раньше было
              «@1» рядом с синей «1» — два бейджа об одном и том же, и красный
              с цифрой не отличался от счётчика. Цифра упоминаний не нужна:
              сколько всего ждёт — говорит синий счётчик рядом.
              Класс is-mention: в активной строке красный НЕ перекрашивается
              в акцент (см. roomListCss), иначе значок пропадает именно
              в открытом чате. Упоминания в тредах сюда входят: highlightOf
              = getUnreadNotificationCount, а он суммирует и тредовые счётчики. */}
          {room.highlight > 0 && (
            <span
              className="alg-room-badge is-mention"
              style={{ ...s.badge, ...s.mentionBadge, background: color.badgeHighlight }}
              data-testid="room-highlight"
              data-highlight={room.highlight}
              title={t("room.mentioned")}
              aria-label={t("room.mentioned")}
              role="img"
            >
              @
            </span>
          )}
          {/* Циферка — ТОЛЬКО notifying (модель 13:49): личка, упоминания,
              ответы в моих тредах. Обычный групповой шум циферки не даёт —
              о нём говорит жирный текст строки выше. Если всё, что ждёт, —
              это упоминания (notifying === highlight), цифру не дублируем:
              «@» уже сказал главное, а «@ 1» читается как два события. */}
          {room.notifying > 0 && room.notifying !== room.highlight && (
            <span className="alg-room-badge" style={s.badge} data-testid="room-badge">
              {room.notifying > 999 ? "999+" : room.notifying}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/** Подходит ли комната под строку поиска: по имени и по тексту превью. */
function matches(room: RoomSummary, needle: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase();
  return (
    room.name.toLowerCase().includes(n) ||
    room.previewText.toLowerCase().includes(n) ||
    (room.previewSender ?? "").toLowerCase().includes(n)
  );
}

/*
 * Верхний предел ширины колонки списка — ДОЛЯ ОКНА, а не жёсткие пиксели
 * (Антон 2026-10-05: «колонка не раздвигается», жёсткий потолок был 560px).
 *
 * Формула: половина ширины окна минус рельс (56px) — так лента всегда
 * получает не меньше половины окна, а колонка может вырасти до разумного
 * «широкого списка». Абсолютный потолок 960px — для очень широких
 * мониторов (≥2К): дальше строки превью становятся слишком длинными и
 * читаются хуже, а пустое место полезнее отдать ленте.
 *
 * Пересчёт на resize: окно могут сузить уже после того, как колонка
 * раздвинута, — usePanelWidth переклампливает сохранённое значение при
 * смене max, поэтому колонка сама вернётся в пределы без ручного тянута.
 */
const SIDEBAR_MAX_ABS = 960;
function useSidebarMaxWidth(): number {
  const compute = () => {
    if (typeof window === "undefined") return SIDEBAR_MAX_ABS;
    return Math.min(SIDEBAR_MAX_ABS, Math.max(320, Math.floor(window.innerWidth / 2) - size.railWidth));
  };
  const [max, setMax] = useState<number>(compute);
  useEffect(() => {
    const onResize = () => setMax(compute());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return max;
}

export function RoomList({
  invites,
  joined,
  spaces,
  totals,
  activeRoomId,
  onSelect,
  onSelectEvent,
  loading,
  onCreateRoom,
  onCreateDm,
  onCreateSpace,
  railBottom,
  onActiveSpaceChange,
  onToggleFavourite,
  onInvite,
  onRoomLink,
  onLeave,
  onAcceptInvite,
  onDeclineInvite,
  onMoveToSpace,
  onSpaceMenu,
  onSpaceInvite,
  onSpaceRename,
  onSpaceMembers,
  canSpaceInvite,
  onSpaceLeave,
  mobileHeaderAvatar,
  onMobileOpenSettings,
}: {
  invites: RoomSummary[];
  joined: RoomSummary[];
  spaces: SpaceInfo[];
  /** Готовые агрегаты из buildRoomLists — единый источник цифр навигации. */
  totals: RoomLists["totals"];
  activeRoomId: string | null;
  onSelect: (roomId: string) => void;
  /**
   * Переход в комнату К КОНКРЕТНОМУ СООБЩЕНИЮ — клик по результату поиска
   * по содержимому. Отдельно от onSelect, потому что тому нечем передать
   * eventId, а менять его сигнатуру значит трогать все вызовы выбора
   * комнаты. Не передали — результат просто откроет комнату (onSelect).
   */
  onSelectEvent?: (roomId: string, eventId: string) => void;
  loading: boolean;
  onCreateRoom: () => void;
  onCreateDm: () => void;
  /**
   * Создание пространства. На ДЕСКТОПЕ — кнопка в шапке колонки и кружок
   * внизу рельсы; на МОБИЛЬНОМ — только пункт меню общей кнопки «+»
   * (правка Антона 2026-09-21: текстовые кнопки из мобильной шапки
   * убраны, спейс создаётся ТОЛЬКО через большой «+» наверху).
   * Опционально, чтобы старые вызовы компилировались; без обработчика
   * ни один из входов не рисуется (право mayCreateSpace считает App).
   */
  onCreateSpace?: () => void;
  /**
   * НИЗ ВЕРТИКАЛЬНОЙ РЕЛЬСЫ — готовый ReactNode (правка Антона 12.09
   * 12:26 п.1: свой аватар — самый нижний элемент рельсы, вход в личный
   * кабинет). RoomList не знает про MatrixClient и профиль пользователя,
   * поэтому аватар собирает вызывающий код (App) и передаёт сюда целиком.
   * Прижатие к низу делает сам переданный элемент (marginTop:auto):
   * рельс — flex-колонка, и RoomList просто рендерит узел последним.
   */
  railBottom?: ReactNode;
  /**
   * Какое пространство сейчас выбрано в рельсе слева (null = «Дом»).
   * Поднимаем наружу, потому что от этого зависят ПОДСКАЗКИ в диалогах
   * (Антон 12.09 10:52): «если у меня активирован home — вижу вообще всех;
   * если активировано пространство ALG — вижу всех членов ALG».
   * Без этого диалог не знает, в каком контексте его открыли.
   */
  onActiveSpaceChange?: (spaceId: string | null) => void;
  onToggleFavourite: (room: RoomSummary) => void;
  onInvite: (room: RoomSummary) => void;
  /** «Ссылка на комнату…» в меню строки (Антон 16.10). */
  onRoomLink?: (room: RoomSummary) => void;
  onLeave: (room: RoomSummary) => void;
  onAcceptInvite: (room: RoomSummary) => void;
  onDeclineInvite: (room: RoomSummary) => void;
  /**
   * «Перенести в пространство…» в меню комнаты. Передаётся ТОЛЬКО когда у
   * меня есть права на m.space.child хотя бы в одном пространстве
   * (вызывающий код считает через mayManageSpaceChild). Без прав пункт
   * меню не показывается — модель доступа п.3/4d: клиент не предлагает
   * недопустимое, а реальную границу ставит сервер.
   */
  onMoveToSpace?: (room: RoomSummary) => void;
  /**
   * Правый клик по иконке пространства в рельсе → меню спейса
   * (пригласить / переименовать / выйти). У спейса нет своей строки в
   * списке чатов, поэтому единственное место для его меню — сама иконка.
   * Обработчики действий приходят отдельными пропами ниже, чтобы RoomList
   * сам решал, что показать, а вызывающий код — что сделать.
   */
  onSpaceMenu?: (space: SpaceInfo, x: number, y: number) => void;
  /** Действия меню пространства. Без них меню не рисуется вовсе. */
  onSpaceInvite?: (space: SpaceInfo) => void;
  onSpaceRename?: (space: SpaceInfo) => void;
  onSpaceMembers?: (space: SpaceInfo) => void;
  /** Права на пункты меню спейса; не передано = считаем, что можно.
      canSpaceRename БОЛЬШЕ НЕ ИСПОЛЬЗУЕТСЯ (Антон 12:56): вход в
      настройки открыт всем, потому что внутри живёт «Выйти». */
  canSpaceInvite?: (space: SpaceInfo) => boolean;
  canSpaceRename?: (space: SpaceInfo) => boolean;
  onSpaceLeave?: (space: SpaceInfo) => void;
  /**
   * МОБИЛЬНАЯ ШАПКА (правка Антона 2026-09-20 19:17, п.2): аватар слева
   * (тап → личный кабинет, как на десктопе внизу рельсы) и «колесо»
   * настроек ⚙️ справа рядом с «+». RoomList не знает про MatrixClient
   * и профиль — как и railBottom, оба приходят готовыми из App.
   * Только narrow; десктоп не меняется (там аватар в рельсе, а вход
   * в настройки — через кабинет).
   */
  mobileHeaderAvatar?: ReactNode;
  onMobileOpenSettings?: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const chatFilters = useMemo<FilterDef[]>(() => [
    { id: "unread", label: t("room.unreadTab"), filter: (r) => r.unread > 0, empty: t("room.emptyUnread") },
    { id: "mentions", label: t("room.mentionsTab"), filter: (r) => r.highlight > 0, empty: t("ui.vas_ni_v_ch_m_ne_upominali") },
    { id: "favorite", label: t("room.favouritesTab"), filter: (r) => r.pinned, empty: t("room.emptyFavourites") },
    { id: "groups", label: t("room.groupsTab"), filter: (r) => !r.direct, empty: t("room.emptyGroups") },
    { id: "people", label: t("chat.private"), filter: (r) => r.direct, empty: t("room.emptyPeople") },
  ], [t]);
  /**
   * ЧИП «МЕНЯ ЖДУТ» (Антон 12.10 15:13: «если нет навигации, я не узнаю, что
   * меня где-то ждут»). Отдельно от выпадашки: виден всегда, одно нажатие.
   * Предикат тот же, что у значка Дока / заголовка (totals.allUnread в
   * buildRoomLists): join-комнаты с notifying > 0 ПЛЮС все приглашения.
   * Счётчик берём готовый из totals — второй подсчёт не заводим.
   * Не дубль «Упоминаний»: те = highlight > 0 (только @), а здесь ещё и
   * любые непрочитанные личные и ответы в моих тредах.
   */
  const waitingFilter = useMemo<FilterDef>(() => ({
    id: "waiting", label: t("room.waitingChip"), filter: (r) => r.notifying > 0, empty: t("room.emptyWaiting"),
  }), [t]);
  const [tab, setTab] = useState<Tab>("home");
  const [chatFilter, setChatFilter] = useState<ChatFilter | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menu, setMenu] = useState<RoomMenuTarget | null>(null);
  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  // Меню пространства — отдельное состояние от меню комнаты: цели разного
  // типа (SpaceInfo vs RoomSummary), и скрещивать их в одном объекте
  // значило бы половину полей держать «на всякий случай».
  const [spaceMenu, setSpaceMenu] = useState<SpaceMenuTarget | null>(null);
  const openMenu = (room: RoomSummary, x: number, y: number) =>
    setMenu({ room, x, y });
  // Внутренний обработчик правого клика по иконке спейса: используем его,
  // когда есть ХОТЯ БЫ один обработчик действий — иначе меню открылось бы
  // пустым. Внешний onSpaceMenu при этом не нужен: состояние локальное.
  // «Выйти» доступен любому участнику, поэтому меню имеет смысл всегда,
  // когда есть onSpaceLeave. Пункты управления (пригласить/переименовать)
  // подмешиваются по правам — см. canSpaceInvite/canSpaceRename ниже
  // (Антон 12.09 11:49 «available to admins only»).
  const spaceMenuEnabled = Boolean(onSpaceLeave);

  // The filter popup is deliberately local to the room list. Escape returns
  // focus to its trigger; a pointer press outside closes without stealing the
  // user's next click.
  useEffect(() => {
    if (!filterOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target;
      if (target instanceof Node && !filterWrapRef.current?.contains(target)) {
        setFilterOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setFilterOpen(false);
      filterButtonRef.current?.focus();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [filterOpen]);

  /**
   * Счётчики во вкладках — число ЧАТОВ по отбору вкладки (модель 13:49:
   * «единица в навигации — чат»). Отбор у каждой вкладки свой, но цифра
   * пересчитана по новому правилу: ждущие чаты берутся по `notifying`,
   * «Непрочитанные» — по любому непрочитанному (сюда входит и жирный
   * шум: вкладка называется «Непрочитанные», а не «Меня ждут», и жирные
   * строки должны быть где-то найдены). Где можно — берём готовое число
   * из единого агрегата buildRoomLists, а не пересчитываем здесь второй
   * раз (требование «один подсчёт — много потребителей»).
   */

  /**
   * Набор вкладок. Порядок задан требованием:
   *   Home | Unread | <по вкладке на КАЖДЫЙ спейс> | Favorites | People
   *
   * Home — комнаты ВНЕ пространств (spaceIds пуст). Это не «все чаты»:
   * иначе Home дублировал бы объединение всех остальных вкладок и перестал
   * бы что-либо значить. Element трактует Home так же.
   */
  const tabs = useMemo<TabDef[]>(() => {
    const spaceTabs: TabDef[] = spaces.map((sp) => ({
      id: `space:${sp.roomId}` as Tab,
      label: sp.name,
      // Готовое число из buildRoomLists — ЧАТЫ спейса, которые меня ждут
      // (notifying > 0). То же число на иконке спейса в рельсе — один
      // источник, считать второй раз здесь нельзя.
      count: sp.unreadRooms,
      filter: (r) => r.spaceIds.includes(sp.roomId),
      empty: t("room.emptySpace"),
    }));
    return [
      {
        id: "home",
        label: t("room.homeTab"),
        // Из единого агрегата: иконка Home в рельсе показывает то же число.
        count: totals.homeUnread,
        filter: isHomeRoom,
        empty: t("room.emptyOutsideSpaces"),
      },
      ...spaceTabs,
    ];
    // totals.* и joined — из одного buildRoomLists, зависимости полные.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces, totals.homeUnread, t]);

  // Если активная вкладка исчезла (вышли из пространства) — не оставляем
  // человека с пустым списком без объяснений, а возвращаемся на Home.
  useEffect(() => {
    if (!tabs.some((t) => t.id === tab)) setTab("home");
  }, [tabs, tab]);

  const activeDef = useMemo(() => tabs.find((t) => t.id === tab) ?? tabs[0], [tabs, tab]);

  const activeFilterDef = chatFilter === "waiting" ? waitingFilter : chatFilters.find((f) => f.id === chatFilter) ?? null;
  const visible = useMemo(() => {
    // System filters are global, as the former system tabs were. A selected
    // space remains intact in state and is restored when the filter clears.
    const base = activeFilterDef
      ? joined.filter(activeFilterDef.filter)
      : joined.filter(activeDef.filter);
    return query ? base.filter((r) => matches(r, query)) : base;
  }, [joined, activeDef, activeFilterDef, query]);

  // Секции с заголовками, как в Element: «Избранное» отдельно, остальное — «Чаты».
  // На вкладке «Избранное» разделение бессмысленно (там всё избранное), поэтому
  // заголовки там не показываем — иначе получится секция из одного пункта меню.
  const { favourites, chats } = useMemo(() => {
    if (chatFilter === "favorite") return { favourites: [] as RoomSummary[], chats: visible };
    return {
      favourites: visible.filter((r) => r.pinned),
      chats: visible.filter((r) => !r.pinned),
    };
  }, [visible, chatFilter]);

  const visibleInvites = useMemo(
    // Приглашение = «меня ждут» (входит в totals.allUnread), поэтому под
    // чипом «Меня ждут» приглашения остаются; прочие фильтры их прячут.
    () => chatFilter && chatFilter !== "waiting" ? [] : (query ? invites.filter((r) => matches(r, query)) : invites),
    [invites, query, chatFilter],
  );

  const activeSpaceId = !chatFilter && tab.startsWith("space:") ? tab.slice("space:".length) : null;
  // Сообщаем наверх при КАЖДОЙ смене вкладки: диалоги создания читают это
  // значение, чтобы ограничить подсказки выбранным пространством.
  useEffect(() => {
    onActiveSpaceChange?.(activeSpaceId);
  }, [activeSpaceId, onActiveSpaceChange]);

  const narrow = useNarrowScreen();
  /* Room-list width is user-adjustable (Anton 2026-09-17); the rail is not.

     ВЕРХНИЙ ПРЕДЕЛ — ДОЛЯ ОКНА (Антон 2026-10-05): раньше стоял жёсткий
     потолок 560px, и колонка «не раздвигалась». Теперь потолок — половина
     ширины окна минус рельс и минимум для ленты, но не выше абсолютных
     960px (очень широкие мониторы: список на весь экран не нужен, вокруг
     45–55 символов в строке превью читать удобнее всего). Нижний предел
     220px оставлен: аватар 44 + имя с превью ещё читаются, уже — нет.
     На узком экране (narrow) значение не применяется: колонка и так на
     всю ширину через mobileCss, а ручка скрыта. */
  const sidebarMax = useSidebarMaxWidth();
  const [sidebarWidth, setSidebarWidth] = usePanelWidth("roomList", size.sidebarWidth, 220, sidebarMax);

  /**
   * МЕНЮ НА МОБИЛЬНОМ — DROPDOWN ВНИЗ ПОД ЭЛЕМЕНТОМ (уточнение Антона
   * 2026-09-20 19:24, ОТМЕНЯЕТ bottom-sheet 19:23): «раскрывается вниз
   * как пирамидка — узко у кнопки, пункты один под другим». Якорь —
   * координаты элемента/тапа; рендер — DropdownMenu (fixed у якоря,
   * прозрачный ловец для закрытия тапом мимо). `null` = закрыто.
   */
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const mobileCreateBtnRef = useRef<HTMLButtonElement | null>(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState<MenuRect | null>(null);
  /**
   * Мобильное меню ПРОСТРАНСТВА: долгий тап по иконке в горизонтальной
   * строке. Тот же набор пунктов, что в SpaceMenu по правому клику на
   * десктопе. Якорь — точка тача (координаты пальца).
   */
  const [mobileSpaceMenu, setMobileSpaceMenu] = useState<{ space: SpaceInfo; anchor: MenuRect } | null>(null);
  /** Таймер длинного тапа: 500мс удержания = «правый клик» на тач-экране. */
  const longPressRef = useRef<{ space: SpaceInfo; timer: number } | null>(null);
  const cancelLongPress = () => {
    if (longPressRef.current) {
      window.clearTimeout(longPressRef.current.timer);
      longPressRef.current = null;
    }
  };

  /* СУЖЕННАЯ КОЛОНКА ДО АВАТАРОВ (Антон 2026-09-18, п.8).

     Состояние сохраняется в localStorage (требование постановки: «collapsed-режим
     с сохранением состояния») тем же способом и рядом с шириной панели,
     чтобы всё состояние левой колонки жило в одном месте. Ширина развёрнутой
     колонки НЕ теряется: сворачивание не трогает sidebarWidth, и после
     разворачивания колонка возвращается ровно той же, какой была.

     Почему отдельное состояние, а не «ширина меньше N = свёрнуто»: при такой
     связке перетаскивание границы случайно схлопывало бы колонку, а вернуть её
     было бы нечем — ручки ресайза в свёрнутом виде нет. Явная кнопка честнее. */
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ROOM_LIST_COLLAPSED_KEY) === "1";
    } catch {
      // localStorage недоступен (приватный режим) — живём без запоминания.
      return false;
    }
  });
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(ROOM_LIST_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // Состояние всё равно применится на эту сессию.
      }
      return next;
    });
  };
  // На узком экране сворачивать нечего: там колонка и комната и так показываются
  // поочерёдно во всю ширину, и полоска аватаров была бы тупиком.
  const isCollapsed = collapsed && !narrow;
  // Аватар + симметричный воздух. Не меньше: бейдж выступает за край кружка.
  const effectiveWidth = isCollapsed ? size.avatar + 20 : sidebarWidth;

  return (
    <div
      className="alg-room-list-shell"
      data-collapsed={isCollapsed ? "1" : "0"}
      style={{ display: "flex", minHeight: 0, flex: "0 0 auto", minWidth: 0 }}
    >
      <style>{roomListCss}</style>

      {/* ВЕРТИКАЛЬНЫЙ РЕЛЬС. Логотип сверху (слот под мастер-файл), затем
          Home, ниже по круглой иконке на каждый спейс.
          Тот же массив `spaces`, что и у вкладок — рассинхронизироваться нечему.
          НА МОБИЛЬНОМ (narrow) РЕЛЬСА НЕТ (спека 2026-09-20): его заменяет
          горизонтальная строка спейсов над списком + меню «Пространства». */}
      {!narrow && (
      <nav className="alg-rail" style={s.rail} aria-label={t("room.railAria")} data-testid="space-rail">
        {/* ЛОГОТИП УБРАН ИЗ СБОРКИ (Антон, 14.09 14:27: «логотип вообще
            убери из сборки»). Рельс начинается сразу с «Home».
            Мастер-файлы остаются в public/brand/ — они не мешают и нужны,
            если знак решат вернуть; из интерфейса не отдаётся ни один. */}
        {/* Верхний отступ выравнивает центр Home с центром строки поиска
            соседней колонки; рельс больше не начинается над её шапкой. */}
        <div aria-hidden style={{ height: 28, flex: "0 0 28px" }} />
        <RailIcon
          id="home"
          label={t("ui.home_chaty_vne_prostranstv")}
          initials="H"
          // Домик КРУПНО, почти на весь кружок (Антон 19:58: «сделай
          // больше на весь кружок»). 26 из 40 — иконка занимает круг,
          // но не упирается в его край: вплотную выглядело бы обрезанным.
          glyph={<IconHome size={26} />}
          badge={totals.homeUnread}
          active={!chatFilter && tab === "home"}
          onClick={() => {
            setChatFilter(null);
            setTab("home");
          }}
        />
        {spaces.length > 0 && <div style={s.railDivider} />}
        {spaces.map((sp) => (
          <RailIcon
            key={sp.roomId}
            id={sp.roomId}
            label={sp.name}
            initials={sp.initials}
            avatarUrl={sp.avatarUrl}
            // Модель 13:49: на иконке спейса — ЧИСЛО ЧАТОВ, которые ждут.
            badge={sp.unreadRooms}
            highlightBadge={sp.highlight}
            active={!chatFilter && activeSpaceId === sp.roomId}
            onClick={() => {
              setChatFilter(null);
              setTab(`space:${sp.roomId}` as Tab);
            }}
            // Правый клик — меню ПРОСТРАНСТВА (пригласить/переименовать/
            // выйти). Другого места у спейса нет: в список чатов он не
            // попадает (buildRoomLists отделяет его от комнат).
            onContextMenu={
              spaceMenuEnabled || onSpaceMenu
                ? (x, y) => {
                    onSpaceMenu?.(sp, x, y);
                    if (spaceMenuEnabled) setSpaceMenu({ space: sp, x, y });
                  }
                : undefined
            }
          />
        ))}
        {/* Кнопка «＋ Пространство» в НИЗУ рельсы: рядом с иконками спейсов,
            а не в шапке колонки — там уже «＋ Комната»/«＋ Личный чат», и
            третья кнопка разорвала бы ряд. Создания спейса в клиенте раньше
            не было вообще (блокер приёмки, Антон 12.09 11:14). */}
        {onCreateSpace && (
          <button
            type="button"
            className="alg-rail-btn"
            title={t("ui.sozdat_prostranstvo")}
            aria-label={t("ui.sozdat_prostranstvo")}
            data-testid="new-space"
            onClick={onCreateSpace}
            style={{
              background: "transparent",
              border: `1px dashed ${color.borderStrong}`,
              borderRadius: "50%",
              width: size.railIcon,
              height: size.railIcon,
              color: color.textDim,
              fontSize: font.body,
              cursor: "pointer",
              flex: "0 0 auto",
            }}
          >
            +
          </button>
        )}
        {/* Свой аватар — САМЫЙ НИЖНИЙ элемент рельсы (правка 12:26 п.1).
            Приходит готовым из App: там есть client и профиль. */}
        {railBottom}
      </nav>
      )}

      <nav
        className={"alg-room-sidebar" + (isCollapsed ? " is-collapsed" : "")}
        style={{
          ...s.sidebar,
          width: effectiveWidth,
          flex: `0 0 ${effectiveWidth}px`,
          position: "relative",
        }}
        aria-label={t("room.sidebarAria")}
        data-testid="room-sidebar"
      >
        {/* Ручка ресайза только у развёрнутой колонки: тянуть свёрнутую полоску
            аватаров некуда — её ширина задана размером аватара (п.8). */}
        {!isCollapsed && (
          <PanelResizeHandle
            edge="right"
            width={sidebarWidth}
            onResize={setSidebarWidth}
            onReset={() => setSidebarWidth(size.sidebarWidth)}
            testId="resize-room-list"
          />
        )}
        {/* КНОПКА СВОРАЧИВАНИЯ. На узком экране не рисуется вовсе: там режим
            не применяется, и кнопка-пустышка была бы обманом. */}
        {!narrow && (
          <button
            type="button"
            className="alg-collapse-toggle"
            onClick={toggleCollapsed}
            data-testid="room-list-collapse"
            aria-expanded={!isCollapsed}
            title={isCollapsed ? t("room.expandList") : t("room.collapseList")}
            aria-label={isCollapsed ? t("room.expandList") : t("room.collapseList")}
          >
            {/* Шеврон в сторону будущего движения: влево = свернуть. */}
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d={isCollapsed ? "M9 6l6 6-6 6" : "M15 6l-6 6 6 6"}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {/* Шапка колонки (кнопки создания, поиск, фильтры) в свёрнутом виде
            не рисуется: в полоску шириной в аватар поле поиска не влезает, а
            урезанные кнопки превратились бы в загадки. Создание комнаты/чата
            остаётся доступным после разворачивания — один клик. */}
        {!isCollapsed && (
        <div style={s.sidebarHead}>
          {/* МОБИЛЬНАЯ ШАПКА (правка Антона 2026-09-20 19:17, п.2):
              «аватар | название | + | ⚙️» одной строкой НАД поиском.
              Аватар слева — тот же вход в личный кабинет, что на
              десктопе внизу рельсы (App передаёт готовый узел); колесо
              ⚙️ справа открывает тот же кабинет (там живут все
              настройки и «Выйти»). «+» — единственная кнопка создания
              на мобильном (п.1): её меню содержит все три действия.
              На десктопе шапки нет: верхняя панель удалена правкой
              12.09, название приложения нигде не дублируется. */}
          {narrow && (
            <div
              className="alg-mobile-header"
              data-testid="mobile-header"
              style={{
                display: "flex",
                alignItems: "center",
                gap: sp.sm,
                padding: `${sp.md}px ${sp.lg}px 0`,
              }}
            >
              {mobileHeaderAvatar}
              <div
                style={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: color.text,
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                data-testid="mobile-list-title"
              >
                alg(success)
              </div>
              <button
                ref={mobileCreateBtnRef}
                type="button"
                aria-label={t("room.mobileCreateTitle")}
                title={t("room.mobileCreateTitle")}
                aria-haspopup="menu"
                aria-expanded={createMenuOpen}
                data-testid="mobile-create-menu-button"
                onClick={() => {
                  // Якорь — сама кнопка (уточнение 19:24: «вниз под
                  // кнопкой “+”»); rect читаем в момент клика.
                  const r = mobileCreateBtnRef.current?.getBoundingClientRect();
                  setCreateMenuAnchor(r ? { left: r.left, right: r.right, top: r.top, bottom: r.bottom } : null);
                  setCreateMenuOpen((v) => !v);
                }}
                style={{
                  flex: "0 0 auto",
                  width: 36,
                  height: 36,
                  display: "grid",
                  placeItems: "center",
                  border: `1px solid ${color.border}`,
                  borderRadius: radius.circle,
                  background: color.bg,
                  color: color.textDim,
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                <IconPlus size={18} />
              </button>
              {onMobileOpenSettings && (
                <button
                  type="button"
                  aria-label={t("app.settings")}
                  title={t("app.settings")}
                  data-testid="mobile-settings-button"
                  onClick={onMobileOpenSettings}
                  style={{
                    flex: "0 0 auto",
                    width: 36,
                    height: 36,
                    display: "grid",
                    placeItems: "center",
                    border: `1px solid ${color.border}`,
                    borderRadius: radius.circle,
                    background: color.bg,
                    color: color.textDim,
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  <IconSettings size={18} />
                </button>
              )}
            </div>
          )}
          {/* Создание — в шапке колонки, как в Telegram/Element. Две отдельные
              кнопки, а не одна с выпадающим списком: меню из двух пунктов
              добавляет клик и ничего не экономит.
              НА МОБИЛЬНОМ (narrow) ЭТОГО РЯДА НЕТ ВООБЩЕ (правка Антона
              2026-09-21 по скриншоту Android 1080x2640: «убрать кнопки
              быстрого создания из шапки — занимают место и дублируют общий
              плюс»). Раньше ряд скрывался только CSS-классом
              alg-mobile-head-actions; теперь он не рендерится вовсе: все
              три действия (комната / личный чат / пространство) живут в
              меню единственной кнопки «+» в мобильной шапке, и создание
              пространства на телефоне идёт ТОЛЬКО через неё. */}
          {!narrow && (
          <div style={{ ...s.actionRow, padding: `${sp.md}px ${sp.lg}px 0` }}>
            <button
              type="button"
              style={s.actionButton}
              onClick={onCreateRoom}
              data-testid="new-room"
            >{t("room.newRoom")}</button>
            <button
              type="button"
              style={s.actionButton}
              onClick={onCreateDm}
              data-testid="new-dm"
            >{t("room.newDm")}</button>
            {/* «＋ Пространство» ЗДЕСЬ, а не только кружком внизу рельсы
                (Антон 12.09 11:49: «need a + create a space»). Причина:
                два соседних действия подписаны словами, а создание спейса
                было безымянным кружком в другом углу — человек ищет его
                там же, где две первые кнопки, и не находит. Кружок внизу
                рельсы оставлен: он рядом со списком пространств и удобен,
                когда рельса уже в фокусе.
                Это ДЕСКТОПНАЯ шапка: на мобильном весь ряд не рендерится
                (см. {!narrow && …} выше), и создание спейса там идёт через
                пункт меню общей кнопки «+» (mobile-create-space). */}
            {onCreateSpace && (
              <button
                type="button"
                style={s.actionButton}
                onClick={onCreateSpace}
                data-testid="new-space-head"
              >{t("room.spaceCreate")}</button>
            )}
          </div>
          )}
          <div style={{ ...s.searchWrap, display: "flex", alignItems: "center", gap: sp.sm }}>
            <div style={{ position: "relative", flex: "1 1 auto", minWidth: 0 }}>
              <span style={{ ...s.searchIcon, left: 10 }} aria-hidden>🔍</span>
              <input
                className="alg-search-input"
                style={s.searchInput}
                type="search"
                value={query}
                placeholder={t("common.search")}
                aria-label={t("room.searchAria")}
                data-testid="room-search"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {/* МОБИЛЬНАЯ КНОПКА «+» ПЕРЕЕХАЛА В ШАПКУ (правка Антона
                2026-09-20 19:17, п.1): единственная кнопка создания —
                в строке «аватар | название | + | ⚙️» над поиском. В
                строке поиска её дубль убран: рядом с полем остаётся
                только фильтр. */}
            <div ref={filterWrapRef} style={{ position: "relative", flex: "0 0 auto" }}>
              <button
                ref={filterButtonRef}
                type="button"
                className={"alg-filter-button" + (chatFilter ? " is-active" : "")}
                aria-label={activeFilterDef ? tfmt("chat.filterLabel", { value: activeFilterDef.label }) : t("chat.filterChats")}
                title={activeFilterDef ? tfmt("chat.filterLabel", { value: activeFilterDef.label }) : t("chat.filterChats")}
                aria-haspopup="menu"
                aria-expanded={filterOpen}
                aria-controls="room-filter-menu"
                data-testid="room-filter-button"
                onClick={() => setFilterOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key !== "ArrowDown") return;
                  e.preventDefault();
                  setFilterOpen(true);
                  requestAnimationFrame(() => {
                    filterWrapRef.current
                      ?.querySelector<HTMLButtonElement>('[role="menuitemradio"]')
                      ?.focus();
                  });
                }}
              >
                {/* Minimal two-line filter icon from the approved header reference. */}
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9h12M8 15h8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                {chatFilter && <span className="alg-filter-dot" aria-hidden />}
              </button>
              {filterOpen && (
                <div
                  id="room-filter-menu"
                  className="alg-filter-menu"
                  role="menu"
                  aria-label={t("chat.filters")}
                  data-testid="room-filter-menu"
                  onKeyDown={(e) => {
                    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
                    e.preventDefault();
                    const items = Array.from(
                      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]'),
                    );
                    if (!items.length) return;
                    const current = items.indexOf(document.activeElement as HTMLButtonElement);
                    const next = e.key === "Home"
                      ? 0
                      : e.key === "End"
                        ? items.length - 1
                        : e.key === "ArrowDown"
                          ? (current + 1 + items.length) % items.length
                          : (current - 1 + items.length) % items.length;
                    items[next]?.focus();
                  }}
                >
                  {chatFilters.map((filter) => (
                    <button
                      key={filter.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={chatFilter === filter.id}
                      className={"alg-filter-item" + (chatFilter === filter.id ? " is-active" : "")}
                      data-testid={`room-filter-${filter.id}`}
                      onClick={() => {
                        setChatFilter(filter.id);
                        setFilterOpen(false);
                        filterButtonRef.current?.focus();
                      }}
                    >
                      <span>{filter.label}</span>
                      {chatFilter === filter.id && <span aria-hidden>✓</span>}
                    </button>
                  ))}
                  {chatFilter && (
                    <button
                      type="button"
                      role="menuitem"
                      className="alg-filter-clear"
                      onClick={() => {
                        setChatFilter(null);
                        setFilterOpen(false);
                        filterButtonRef.current?.focus();
                      }}
                    >{t("chat.clearFilter")}</button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ЧИП «МЕНЯ ЖДУТ» — на виду над списком, не в выпадашке. При 0
              показываем приглушённым с «0», а не прячем: кнопка стоит на
              одном месте и ряд не прыгает, а «0» сам по себе ответ. */}
          <div style={{ padding: `${sp.sm}px ${sp.lg}px 0`, display: "flex" }}>
            <button
              type="button"
              className={"alg-waiting-chip" + (chatFilter === "waiting" ? " is-active" : "") + (totals.allUnread === 0 ? " is-empty" : "")}
              aria-pressed={chatFilter === "waiting"}
              aria-label={tfmt("room.waitingChipAria", { count: totals.allUnread })}
              data-testid="room-waiting-chip"
              data-count={totals.allUnread}
              onClick={() => setChatFilter((f) => (f === "waiting" ? null : "waiting"))}
            >
              <span className="alg-waiting-chip-label">{t("room.waitingChip")}</span>
              <span className="alg-waiting-chip-count" aria-hidden>{totals.allUnread > 99 ? "99+" : totals.allUnread}</span>
            </button>
          </div>

          {/* ГОРИЗОНТАЛЬНАЯ СТРОКА СПЕЙСОВ (спека 2026-09-20, этап 1):
              замена вертикального рельса на мобильном. Home + круглые
              иконки 40px + кнопка создания пространства (если есть права).
              Длинный тап по иконке — меню пространства dropdown'ом
              под иконкой (19:24). Своё состояние не
              вводим: строка управляет ТЕМ ЖЕ `tab`/`chatFilter`, что рельс
              и текстовые вкладки, — три способа выбрать одно и то же
              (комментарий в шапке файла). */}
          {narrow && (
            <div className="alg-mobile-spaces-bar" data-testid="mobile-spaces-bar" role="tablist" aria-label={t("room.railAria")}>
              <MobileSpaceIcon
                id="home"
                label={t("ui.home_chaty_vne_prostranstv")}
                initials="H"
                glyph={<IconHome size={22} />}
                badge={totals.homeUnread}
                active={!chatFilter && tab === "home"}
                onClick={() => {
                  setChatFilter(null);
                  setTab("home");
                }}
              />
              {spaces.map((sp) => (
                <MobileSpaceIcon
                  key={sp.roomId}
                  id={sp.roomId}
                  label={sp.name}
                  caption={sp.name}
                  initials={sp.initials}
                  avatarUrl={sp.avatarUrl}
                  badge={sp.unreadRooms}
                  active={!chatFilter && activeSpaceId === sp.roomId}
                  onClick={() => {
                    setChatFilter(null);
                    setTab(`space:${sp.roomId}` as Tab);
                  }}
                  onLongPress={
                    spaceMenuEnabled
                      ? (x, y) => setMobileSpaceMenu({ space: sp, anchor: pointRect(x, y) })
                      : undefined
                  }
                  longPressRef={longPressRef}
                  cancelLongPress={cancelLongPress}
                />
              ))}
              {onCreateSpace && (
                <button
                  type="button"
                  title={t("ui.sozdat_prostranstvo")}
                  aria-label={t("ui.sozdat_prostranstvo")}
                  data-testid="mobile-new-space"
                  onClick={onCreateSpace}
                  style={{
                    flex: "0 0 auto",
                    width: size.railIcon,
                    height: size.railIcon,
                    borderRadius: "50%",
                    border: `1px dashed ${color.borderStrong}`,
                    background: "transparent",
                    color: color.textDim,
                    fontSize: 18,
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  +
                </button>
              )}
            </div>
          )}

          {/* ТЕКСТОВЫЕ ВКЛАДКИ СПЕЙСОВ НА МОБИЛЬНОМ УБРАНЫ (правка Антона
              2026-09-20 19:17, п.3): на ≤640px спейсы показываются ОДИН
              раз — горизонтальной строкой иконок с подписями выше.
              Дубль «иконки + текстовые вкладки» на 412px съедал высоту
              и путал (два ряда управляют одним состоянием `tab`).
              Десктоп вкладок не имеет с 2026-09-17 — там их роль у рельса.
              ГОРИЗОНТАЛЬНЫЙ ряд фильтров (Непрочитанные/Избранное/…) —
              это chatFilters над списком, он остаётся. */}
        </div>
        )}

        {/* СВЁРНУТЫЙ СПИСОК — только аватары (п.8). Отбор комнат тот же самый
            (`visible`): активная вкладка/фильтр продолжают действовать, просто их
            нечем переключить без разворачивания. Избранные сверху без заголовков
            секций: текстовый заголовок в полоске шириной в аватар не читается.
            Приглашения идут первыми и тут, но без кнопок Принять/Отклонить:
            две кнопки в 40px не помещаются, а ошибочное нажатие здесь стоит дорого
            — клик по аватару открывает комнату, где кнопки на месте. */}
        {isCollapsed ? (
          <div style={s.roomScroll} data-testid="room-scroll">
            {visibleInvites.map((r) => (
              <CollapsedRow
                key={r.roomId}
                room={r}
                active={r.roomId === activeRoomId}
                onSelect={onSelect}
                onMenu={openMenu}
              />
            ))}
            {visibleInvites.length > 0 && <div style={s.sectionDivider} />}
            {favourites.map((r) => (
              <CollapsedRow
                key={r.roomId}
                room={r}
                active={r.roomId === activeRoomId}
                onSelect={onSelect}
                onMenu={openMenu}
              />
            ))}
            {favourites.length > 0 && chats.length > 0 && <div style={s.sectionDivider} />}
            {chats.map((r) => (
              <CollapsedRow
                key={r.roomId}
                room={r}
                active={r.roomId === activeRoomId}
                onSelect={onSelect}
                onMenu={openMenu}
              />
            ))}
          </div>
        ) : (
        <div style={s.roomScroll} data-testid="room-scroll">
          {/* Приглашения держим наверху во всех вкладках: это действие, требующее
              ответа, и прятать его за фильтром было бы вредно. Аналога в Telegram
              нет — там приглашений не существует как сущности. */}
          {visibleInvites.length > 0 && (
            <>
              <div style={s.sectionLabel} data-testid="section-invites">
                {tfmt("room.invitesCount", { count: visibleInvites.length })}
              </div>
              {visibleInvites.map((r) => (
                <div key={r.roomId}>
                  <Row
                    room={r}
                    active={r.roomId === activeRoomId}
                    onSelect={onSelect}
                    onMenu={openMenu}
                  />
                  {/* Кнопки ПРЯМО В СПИСКЕ, а не только в шапке открытой комнаты:
                      приглашение — действие, требующее ответа, и заставлять ради него
                      сначала открывать комнату — лишний шаг на ровном месте. */}
                  <div style={s.inviteBar}>
                    <button
                      type="button"
                      style={{ ...s.actionButton, background: color.ok, color: color.textOnAccent, border: "none" }}
                      onClick={() => onAcceptInvite(r)}
                      data-testid="invite-accept"
                      data-room-id={r.roomId}
                    >{t("common.accept")}</button>
                    <button
                      type="button"
                      style={s.actionButton}
                      onClick={() => onDeclineInvite(r)}
                      data-testid="invite-decline"
                      data-room-id={r.roomId}
                    >{t("common.decline")}</button>
                  </div>
                </div>
              ))}
              <div style={s.sectionDivider} />
            </>
          )}

          {favourites.length > 0 && (
            <>
              <div style={s.sectionLabel} data-testid="section-favourites">{t("room.favouritesTab")}</div>
              {favourites.map((r) => (
                <Row
                  key={r.roomId}
                  room={r}
                  active={r.roomId === activeRoomId}
                  onSelect={onSelect}
                  onMenu={openMenu}
                />
              ))}
            </>
          )}

          {chats.length > 0 && (
            <>
              {/* Заголовок «Чаты» показываем только когда выше есть «Избранное»:
                  единственная секция в списке заголовка не требует. */}
              {favourites.length > 0 && (
                <div style={s.sectionLabel} data-testid="section-chats">{t("room.sectionChats")}</div>
              )}
              {chats.map((r) => (
                <Row
                  key={r.roomId}
                  room={r}
                  active={r.roomId === activeRoomId}
                  onSelect={onSelect}
                  onMenu={openMenu}
                />
              ))}
            </>
          )}

          {visible.length === 0 && visibleInvites.length === 0 && (
            <div style={s.emptyHint} data-testid="room-empty">
              {loading
                ? t("room.loadingChats")
                : query
                  ? tfmt("room.emptySearch", { query })
                  : activeFilterDef?.empty ?? activeDef.empty}
            </div>
          )}

          {/* ПОИСК ПО СОДЕРЖИМОМУ — отдельной секцией под списком комнат
              (Антон 24.09 13:30). Совпадения по ИМЕНИ комнаты остаются
              выше и работают как раньше, локально; сообщения ищет сервер
              (POST /_matrix/client/v3/search). Секция сама решает, что
              показать: идёт поиск / пусто / ошибка — и никогда не молчит. */}
          <MessageSearchResults
            query={query}
            onOpen={(roomId, eventId) => {
              if (onSelectEvent) onSelectEvent(roomId, eventId);
              else onSelect(roomId);
            }}
          />
        </div>
        )}

        {/* Подвал со счётчиком чатов — текстовая строка, в свёрнутом виде нечитаема. */}
        {!isCollapsed && (
          <div style={s.sidebarFoot} data-testid="rooms-header">
            {loading
              ? t("room.loadingShort")
              : tfmt("room.chatsCount", { visible: visible.length, joined: joined.length })}
          </div>
        )}
      </nav>

      {/* Меню КОМНАТЫ: на десктопе — плавающее у курсора (правый клик),
          на мобильном — DROPDOWN ВНИЗ ПОД ЭЛЕМЕНТОМ (уточнение Антона
          2026-09-20 19:24, отменяет bottom-sheet 19:23): «пирамидка»
          под строкой, по которой сделали долгий тап; якорь — координаты
          тача, они же у правого клика. */}
      {menu && !narrow && (
        <RoomMenu
          target={menu}
          onClose={() => setMenu(null)}
          onToggleFavourite={onToggleFavourite}
          onInvite={onInvite}
          onRoomLink={onRoomLink}
          onLeave={onLeave}
          onAcceptInvite={onAcceptInvite}
          onDeclineInvite={onDeclineInvite}
          onMoveToSpace={onMoveToSpace}
        />
      )}
      {menu && narrow && (
        <DropdownMenu
          anchor={pointRect(menu.x, menu.y)}
          align="start"
          title={menu.room.name}
          testId="dropdown-room-menu"
          onClose={() => setMenu(null)}
        >
          {(() => {
            const room = menu.room;
            const go = (fn: (r: RoomSummary) => void) => () => {
              setMenu(null);
              fn(room);
            };
            return room.invite ? (
              <>
                <DropdownItem
                  label={t("room.acceptInviteMenu")}
                  onClick={go(onAcceptInvite)}
                  testId="menu-accept"
                />
                <DropdownItem
                  label={t("room.declineInviteMenu")}
                  danger
                  onClick={go(onDeclineInvite)}
                  testId="menu-decline"
                />
              </>
            ) : (
              <>
                <DropdownItem
                  label={room.pinned ? t("room.unfavouriteMenu") : t("room.favouriteMenu")}
                  onClick={go(onToggleFavourite)}
                  testId={room.pinned ? "menu-unfavourite" : "menu-favourite"}
                />
                <DropdownItem
                  label={t("room.inviteMenu")}
                  onClick={go(onInvite)}
                  testId="menu-invite"
                />
                {onRoomLink && (
                  <DropdownItem
                    label={t("room.linkMenu")}
                    onClick={go(onRoomLink)}
                    testId="menu-room-link"
                  />
                )}
                {onMoveToSpace && (
                  <DropdownItem
                    label={t("ui.perenesti_v_prostranstvo")}
                    onClick={go(onMoveToSpace)}
                    testId="menu-move-to-space"
                  />
                )}
                <DropdownItem
                  label={t("room.leaveMenu")}
                  danger
                  onClick={go(onLeave)}
                  testId="menu-leave"
                />
              </>
            );
          })()}
        </DropdownMenu>
      )}
      {spaceMenu && onSpaceLeave && !narrow && (
        <SpaceMenu
          target={spaceMenu}
          onClose={() => setSpaceMenu(null)}
          // Права считает вызывающий код (App) — здесь только «дали или нет».
          onInvite={canSpaceInvite?.(spaceMenu.space) === false ? undefined : onSpaceInvite}
          // НАСТРОЙКИ ДОСТУПНЫ ВСЕМ (Антон 12.09 12:56). Раньше пункт
          // был admins-only, но теперь внутри лежат «Участники» и «Выйти» —
          // действия любого участника. Скрыв пункт у не-админа, мы бы отняли
          // у него единственный способ выйти. Сами поля внутри закрыты
          // правами в RoomSettings (кнопки «Изменить» нет + пояснение).
          onRename={onSpaceRename}
          onMembers={onSpaceMembers}
          onLeave={onSpaceLeave}
        />
      )}

      {/* МОБИЛЬНОЕ МЕНЮ «+» — DROPDOWN ВНИЗ ПОД КНОПКОЙ (уточнение
          19:24: «как пирамидка — узко у кнопки, пункты один под другим»,
          НЕ bottom-sheet). Якорь — сама кнопка «+» в шапке (ref).
          Десктоп меню не знает: там три подписанные кнопки в шапке. */}
      {narrow && createMenuOpen && (
        <DropdownMenu
          anchor={createMenuAnchor ?? { left: 0, right: 0, top: 0, bottom: 0 }}
          align="end"
          title={t("room.mobileCreateTitle")}
          testId="dropdown-create-menu"
          onClose={() => setCreateMenuOpen(false)}
        >
          <DropdownItem
            label={t("room.mobileCreateRoom")}
            onClick={() => {
              setCreateMenuOpen(false);
              onCreateRoom();
            }}
            testId="mobile-create-room"
          />
          <DropdownItem
            label={t("room.mobileCreateDm")}
            onClick={() => {
              setCreateMenuOpen(false);
              onCreateDm();
            }}
            testId="mobile-create-dm"
          />
          {onCreateSpace && (
            <DropdownItem
              label={t("room.mobileCreateSpace")}
              onClick={() => {
                setCreateMenuOpen(false);
                onCreateSpace();
              }}
              testId="mobile-create-space"
            />
          )}
        </DropdownMenu>
      )}

      {/* МОБИЛЬНОЕ МЕНЮ ПРОСТРАНСТВА — тоже DROPDOWN под иконкой
          (уточнение 19:24): долгий тап по иконке в горизонтальной
          строке, якорь — координаты тача. Пункты — те же, что в
          SpaceMenu по правому клику на десктопе. */}
      {narrow && mobileSpaceMenu && onSpaceLeave && (
        <DropdownMenu
          anchor={mobileSpaceMenu.anchor}
          align="start"
          title={mobileSpaceMenu.space.name}
          testId="dropdown-space-menu"
          onClose={() => setMobileSpaceMenu(null)}
        >
          {(canSpaceInvite?.(mobileSpaceMenu.space) !== false) && onSpaceInvite && (
            <DropdownItem
              label={t("room.inviteMenu")}
              onClick={() => {
                const sp = mobileSpaceMenu.space;
                setMobileSpaceMenu(null);
                onSpaceInvite(sp);
              }}
              testId="mobile-space-invite"
            />
          )}
          {onSpaceMembers && (
            <DropdownItem
              label={t("room.spaceMembers")}
              onClick={() => {
                const sp = mobileSpaceMenu.space;
                setMobileSpaceMenu(null);
                onSpaceMembers(sp);
              }}
              testId="mobile-space-members"
            />
          )}
          {onSpaceRename && (
            <DropdownItem
              label={t("room.spaceRename")}
              onClick={() => {
                const sp = mobileSpaceMenu.space;
                setMobileSpaceMenu(null);
                onSpaceRename(sp);
              }}
              testId="mobile-space-settings"
            />
          )}
          <DropdownItem
            label={t("room.leaveMenu")}
            danger
            onClick={() => {
              const sp = mobileSpaceMenu.space;
              setMobileSpaceMenu(null);
              onSpaceLeave(sp);
            }}
            testId="mobile-space-leave"
          />
        </DropdownMenu>
      )}
    </div>
  );
}

/**
 * Иконка спейса в ГОРИЗОНТАЛЬНОЙ строке (мобильная замена рельса,
 * спека 2026-09-20). Тот же круг 40px, что в рельсе; бейдж непрочитанного
 * в правом нижнем углу. Длинный тап (500мс) = «правый клик»: открывает
 * меню пространства. Отдельный компонент, а не переиспользование
 * RailIcon: у RailIcon активность — риска слева (::before в CSS),
 * здесь подчёркивание снизу, и логика длинного тапа в рельсе не нужна.
 */
function MobileSpaceIcon({
  id,
  label,
  caption,
  initials,
  avatarUrl,
  badge,
  active,
  glyph,
  onClick,
  onLongPress,
  longPressRef,
  cancelLongPress,
}: {
  id: string;
  label: string;
  /** Подпись под иконкой (правка 19:17 п.3): без неё круглые иконки
      спейсов на мобильном неотличимы. Не передана — рисуем только круг
      (Home: иконка-домик сама себя объясняет). */
  caption?: string;
  initials: string;
  avatarUrl?: string | null;
  badge: number;
  active: boolean;
  glyph?: ReactNode;
  onClick: () => void;
  /** Долгий тап: координаты тача — якорь dropdown-меню (19:24). */
  onLongPress?: (x: number, y: number) => void;
  longPressRef?: React.MutableRefObject<{ space: SpaceInfo; timer: number } | null>;
  cancelLongPress?: () => void;
}) {
  const client = useMatrixClient();
  const url = useAuthedAvatar(client, avatarUrl ?? null, size.railIcon);
  const inner = url ? (
    <img
      src={url}
      alt=""
      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
    />
  ) : (
    // КРУГ-ЗАГЛУШКА с ОДНОЙ буквой (правка Антона 2026-09-21): initials
    // для спейсов теперь одна буква (spaceLetterOf в rooms.ts). Буква
    // крупная (17px на круге 40px): двухбуквенные инициалы 14px со
    // скриншота Антона выглядели «сырым текстовым блоком», а не аватаром.
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        background: glyph ? color.tgBlue : colorOf(id),
        color: color.textOnAccent,
        fontSize: 17,
        fontWeight: font.weightBold,
        lineHeight: 1,
        overflow: "hidden",
        userSelect: "none",
      }}
      aria-hidden
    >
      {glyph ?? initials}
    </div>
  );
  const icon = (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      title={label}
      aria-label={label}
      data-testid="mobile-space-icon"
      data-space-id={id}
      onClick={onClick}
      onPointerDown={
        onLongPress && longPressRef && cancelLongPress
          ? (e) => {
              cancelLongPress();
              const x = e.clientX;
              const y = e.clientY;
              const timer = window.setTimeout(() => {
                longPressRef.current = null;
                onLongPress(x, y);
              }, 500);
              // space в ref не используется таймером — он важен только
              // как маркер «ждём длинный тап»; храним для симметрии API.
              longPressRef.current = { space: { roomId: id } as SpaceInfo, timer };
            }
          : undefined
      }
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onContextMenu={(e) => {
        // Браузерное контекстное меню на тач-экране — двойник длинного
        // тапа; гасим, чтобы не открывались два меню сразу.
        e.preventDefault();
      }}
      style={{
        position: "relative",
        flex: "0 0 auto",
        width: size.railIcon,
        height: size.railIcon,
        borderRadius: "50%",
        // Активность — boxShadow-КОЛЬЦО вокруг круга, а не border: border
        // 2px съедал content-box (36px из 40px), и между кругом и рамкой
        // оставалась прозрачная щель — кружок «плавал» внутри ободка.
        border: "none",
        boxShadow: active ? `0 0 0 2px ${color.tgBlue}` : "none",
        padding: 0,
        background: "transparent",
        cursor: "pointer",
        touchAction: "manipulation",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      {inner}
      {badge > 0 && (
        <span
          style={{
            position: "absolute",
            right: -2,
            bottom: -2,
            minWidth: 18,
            height: 18,
            padding: "0 4px",
            boxSizing: "border-box",
            borderRadius: 9,
            background: color.tgBlue,
            color: color.textOnAccent,
            fontSize: font.micro,
            fontWeight: font.weightBold,
            lineHeight: "14px",
            textAlign: "center",
            border: `2px solid ${color.sidebarBg}`,
          }}
          data-testid="mobile-space-badge"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
  if (!caption) return icon;
  return (
    <div
      style={{
        flex: "0 0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        width: 56,
      }}
    >
      {icon}
      <span
        aria-hidden
        data-testid="mobile-space-caption"
        style={{
          maxWidth: 56,
          fontSize: font.micro,
          lineHeight: 1.2,
          color: active ? color.tgBlue : color.textDim,
          fontWeight: active ? font.weightBold : undefined,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "center",
        }}
      >
        {caption}
      </span>
    </div>
  );
}

/**
 * МОБИЛЬНЫЙ DROPDOWN — общий каркас всех контекстных меню на ≤640px
 * (правка Антона 2026-09-20 19:24): НЕ bottom-sheet, а «пирамидка» —
 * список раскрывается ВНИЗ ПОД элементом, по которому нажали, пункты
 * один под другим, закрытие тапом вне. Десктоп не тронут: там те же
 * меню остаются плавающими окнами у курсора (RoomMenu/SpaceMenu).
 *
 * ЯКОРЬ — MenuRect: для кнопки «+» это её getBoundingClientRect
 * (прочитан в момент клика, align="end" прижимает правый край меню
 * к правому краю кнопки — «выравнивание по правому краю кнопки»
 * дословно из постановки); для долгого тапа — точка пальца через
 * pointRect (align="start": меню растёт вправо от точки удержания).
 * Меню прижато к якорю СНИЗУ (top = anchor.bottom + 4px зазор); если
 * внизу не хватает высоты — поднимается настолько, чтобы влезть
 * (8px от нижнего края экрана), а избыток уходит во внутреннюю
 * прокрутку. Переворота «вверх над якорем» нет: спека требует вниз.
 *
 * ЗАКРЫТИЕ — тап вне (прозрачный фуллскрин-ловец под меню) и Esc.
 * Затемнения фона нет сознательно: dropdown не модальный, список под
 * ним остаётся виден и живой — в отличие от bottom sheet, который
 * перекрывал весь экран. Подписка на pointerdown отложена на тик по той
 * же причине, что в RoomMenu (см. комментарий там): иначе открывающий
 * тап тут же закроет меню обратно.
 */

/**
 * Прямоугольник якоря dropdown-меню. Для кнопки — её
 * getBoundingClientRect; для долгого тапа — точка пальца как
 * прямоугольник нулевого размера (меню встаёт ПОД точкой удержания).
 */
type MenuRect = { left: number; right: number; top: number; bottom: number };
function pointRect(x: number, y: number): MenuRect {
  return { left: x, right: x, top: y, bottom: y };
}

function DropdownMenu({
  anchor,
  align,
  onClose,
  title,
  testId,
  children,
}: {
  /** Прямоугольник якоря (кнопка или точка тача через pointRect). */
  anchor: MenuRect;
  /**
   * К какому краю якоря прижать меню: «end» — правый край меню по
   * правому краю якоря (кнопка «+» у правого края шапки); «start» —
   * левый край меню под левым краем якоря / точкой тача.
   */
  align: "start" | "end";
  onClose: () => void;
  title?: string;
  testId?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const id = window.setTimeout(() => {
      window.addEventListener("pointerdown", onClose);
      window.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("pointerdown", onClose);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuW = r.width;
    const menuH = r.height;
    const gap = 4;
    const edge = 8;
    // Выравнивание по краю якоря (см. проп align); якорь уже прочитан
    // вызывающим кодом в момент жеста (клик по кнопке / долгий тап).
    let left = align === "end" ? anchor.right - menuW : anchor.left;
    // Не даём уехать за боковые края экрана (страховка для якорей у
    // самого края).
    left = Math.max(edge, Math.min(left, window.innerWidth - menuW - edge));
    let top = anchor.bottom + gap;
    let maxHeight = window.innerHeight - top - edge;
    if (menuH > maxHeight) {
      // Внизу не влезло: прижимаем низ меню к 8px над нижним краем,
      // а избыток высоты уходит во внутреннюю прокрутку. Это НЕ
      // переворот «вверх над якорем»: спека требует раскрытие вниз,
      // меню просто укорачивается.
      top = Math.max(edge, window.innerHeight - menuH - edge);
      maxHeight = window.innerHeight - top - edge;
    }
    setPos({ left, top, maxHeight: Math.max(120, maxHeight) });
  }, [anchor, align]);

  // Начальная позиция до первого измерения: под якорем, меню скрыто
  // (visibility) — техника та же, что у RoomMenu через useLayoutEffect,
  // чтобы не мигнуть в неправильной позиции.
  const initialLeft = align === "end" ? anchor.right - 240 : anchor.left;
  const initialTop = anchor.bottom + 4;

  return (
    <div
      className="alg-dropdown-catcher"
      role="presentation"
      // Тап по ловцу = тап вне меню. Ловец прозрачный: затемнение —
      // свойство bottom sheet и сюда не переезжает.
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className="alg-dropdown-menu"
        role="menu"
        aria-label={title}
        data-testid={testId ?? "dropdown-menu"}
        style={{
          left: pos?.left ?? initialLeft,
          top: pos?.top ?? initialTop,
          visibility: pos ? "visible" : "hidden",
          maxHeight: pos?.maxHeight,
        }}
        // События внутри меню не достигают window-ловца и не закрывают
        // его из-под пальца.
        onPointerDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** Пункт мобильного dropdown-меню. */
function DropdownItem({
  label,
  danger,
  onClick,
  testId,
}: {
  label: string;
  danger?: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="alg-dropdown-item"
      style={danger ? { color: color.danger } : undefined}
      onClick={onClick}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

/** Ширина всей левой стороны (рельс + список) — нужна разметке App. */
export const LEFT_WIDTH = size.railWidth + size.sidebarWidth;
